/**
 * Cookie Consent Management
 *
 * Stores consent decisions in localStorage with a 6-month expiry.
 * No full IP address is stored. Only a random consent ID, timestamp,
 * consent version, and selected categories.
 */

export const CONSENT_VERSION = "spy-consent-2026-08-17-v1";
export const CONSENT_STORAGE_KEY = "spyclean-consent";
export const CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 months

export type ConsentCategory =
  "necessary" | "statistics" | "marketing" | "external_media";

export interface ConsentRecord {
  consentId: string;
  timestamp: string; // UTC ISO
  version: string;
  categories: Record<ConsentCategory, boolean>;
  purposes: Record<ConsentCategory, string>;
  updatedAt: string | null; // UTC ISO if changed later
}

const PURPOSES: Record<ConsentCategory, string> = {
  necessary:
    "Technisch notwendige Funktionen und Speicherung der Consent-Auswahl",
  statistics: "Statistische Auswertung der Website-Nutzung (Google Analytics)",
  marketing:
    "Messung von Werbeanzeigen und Zielgruppenbildung (Google Ads, Meta Pixel)",
  external_media:
    "Einbindung externer Medien (YouTube-Videos über youtube-nocookie.com)",
};

function generateConsentId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDefaultCategories(): Record<ConsentCategory, boolean> {
  return {
    necessary: true,
    statistics: false,
    marketing: false,
    external_media: false,
  };
}

export function getConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ConsentRecord;

    // Check expiry
    const age = Date.now() - new Date(parsed.timestamp).getTime();
    if (age > CONSENT_MAX_AGE_MS) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    // Check version mismatch — re-prompt if version changed
    if (parsed.version !== CONSENT_VERSION) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function hasConsent(): boolean {
  return getConsent() !== null;
}

export function isCategoryEnabled(category: ConsentCategory): boolean {
  const consent = getConsent();
  if (!consent) return category === "necessary";
  return consent.categories[category] ?? false;
}

export function saveConsent(
  categories: Record<ConsentCategory, boolean>,
): ConsentRecord {
  const existing = getConsent();
  const now = new Date().toISOString();

  const record: ConsentRecord = {
    consentId: existing?.consentId ?? generateConsentId(),
    timestamp: existing?.timestamp ?? now,
    version: CONSENT_VERSION,
    categories: { ...categories, necessary: true },
    purposes: PURPOSES,
    updatedAt: existing ? now : null,
  };

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(
    new CustomEvent("spyclean-consent-change", { detail: record }),
  );
  return record;
}

export function revokeConsent(): ConsentRecord {
  return saveConsent(getDefaultCategories());
}

/**
 * Returns consent metadata for CRM association.
 * Only used when a user submits a contact form.
 */
export function getConsentMetadata() {
  const consent = getConsent();
  if (!consent) return null;
  return {
    consent_id: consent.consentId,
    cookie_statistics: consent.categories.statistics,
    cookie_marketing: consent.categories.marketing,
    cookie_external_media: consent.categories.external_media,
    cookie_consent_timestamp: consent.timestamp,
  };
}
